import React, { useEffect, useState } from 'react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { Textarea } from '@/design-system/textarea';
import { toast } from '@/design-system/toaster';
import { Invoice, invoicesAPI } from '@/services/api/invoices';

interface ResolveModalProps {
  isOpen: boolean;
  invoice: Invoice | null;
  onClose: () => void;
  onUpdated: (invoice: Invoice) => void;
}

const ResolveModal: React.FC<ResolveModalProps> = ({
  isOpen,
  invoice,
  onClose,
  onUpdated,
}) => {
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setNote('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen || !invoice) return null;

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const updated = await invoicesAPI.resolve(invoice.id, note.trim() || null);
      toast.success('Invoice resolved', {
        description: `${updated.invoiceNumber} marked resolved. It will appear on the next export.`,
      });
      onUpdated(updated);
      onClose();
    } catch (err) {
      toast.error('Could not resolve invoice', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-checkbox-circle-line" />}
      iconTone="success"
      title="Resolve invoice"
      subtitle={`${invoice.invoiceNumber} · ${invoice.client.name}`}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button tone="neutral" style="stroke" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Resolving…' : 'Mark resolved'}
          </Button>
        </div>
      }
    >
      <div>
        <label
          htmlFor="resolve-note"
          className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600"
        >
          Note (optional)
        </label>
        <Textarea
          id="resolve-note"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. client confirmed receipt by phone, batch reconciled."
        />
      </div>
    </FormModal>
  );
};

export default ResolveModal;
